/**
 * Shared read-only pill used both in the composer (inside TipTap as a node
 * view) and in rendered chat history (via UserMessageBody).
 */
import { FileText, Folder, Layers, X } from "lucide-react";
import { cn } from "@/lib/utils";

export type CorpusChipKind = "doc" | "folder" | "bucket";

export type CorpusChipData = {
  kind: CorpusChipKind;
  id: string;
  label: string;
};

const KindIcon = ({ kind, className }: { kind: CorpusChipKind; className?: string }) => {
  if (kind === "folder") return <Folder className={className} />;
  if (kind === "bucket") return <Layers className={className} />;
  return <FileText className={className} />;
};

export const CorpusChipPill = ({
  kind,
  label,
  onRemove,
  contentEditable = false,
  title,
  surface = "default",
}: {
  kind: CorpusChipKind;
  label: string;
  onRemove?: () => void;
  contentEditable?: boolean;
  title?: string;
  surface?: "default" | "assistant";
}) => {
  const isAssistantSurface = surface === "assistant";

  return (
    <span
      className={cn(
        "inline-flex max-w-[240px] items-center gap-1 rounded-md border px-1.5 py-0.5 align-baseline text-[12px] leading-4 text-foreground",
        isAssistantSurface
          ? "mx-0.5 border-primary/45 bg-primary/15 font-medium shadow-sm ring-1 ring-primary/10 dark:border-primary/55 dark:bg-primary/20"
          : "border-primary/30 bg-primary/10"
      )}
      contentEditable={contentEditable}
      title={title ?? label}
      data-corpus-chip-surface={surface}
    >
      <KindIcon kind={kind} className="h-3 w-3 shrink-0 text-primary" />
      <span className="truncate">{label}</span>


      {onRemove ? (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
          className="text-muted-foreground hover:text-foreground"
          aria-label={`Remove ${label}`}
          tabIndex={-1}
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
    </span>
  );
};
